/**
 * Espejo de worker.js `templateUsesWishlistGeoCta` + COUNTRY_TEMPLATE_MAP
 * (subset). Regla canónica: el Hero CTA per-geo (cta_type wishlist/dual/
 * whatsapp/etc.) SOLO aplica en BaseTemplate. Los otros templates tienen
 * flujos propios (dual variant A/B, bio links hardcoded, etc.) que NO
 * consumen el resolveado per-geo. Ver worker.js:12-26 para racional.
 */

import { normalizeUsStateAbbr } from "./usStateAbbr.js";
import { normalizeCtaCityKey } from "./geoHelpers.js";

export const HERO_CTA_COMPATIBLE_TEMPLATES: ReadonlySet<string> = new Set([
  "BaseTemplate",
]);

export function templateUsesHeroCta(templateName: string | null | undefined): boolean {
  if (!templateName) return false;
  return HERO_CTA_COMPATIBLE_TEMPLATES.has(templateName);
}

/** Templates con funnel dual propio en home — espejo de worker.js templateIsDualSelfContained. */
export function templateIsDualSelfContained(
  templateName: string | null | undefined,
): boolean {
  if (!templateName) return false;
  return (
    templateName === "DualCTAVideoLanding" ||
    templateName === "VideoTemplate" ||
    templateName === "BaseTemplateInternationalDual" ||
    templateName === "USExclusiveLanding"
  );
}

export function templateUsesVariantOnHome(templateName: string | null | undefined): boolean {
  if (!templateName) return false;
  // BaseTemplateInternationalDual incluido aunque su componente .tsx no lea
  // dualVariant: Index.tsx (src/pages/Index.tsx:107) lo promueve runtime a
  // <VideoTemplate /> que SÍ lo consume. Sin incluirlo acá, el admin recibía
  // warnings falsos ("⚠ variant invisible en /") al asignar variantes a
  // países con este template — y la variante SÍ se rendererizaba via upgrade.
  // Espejo del gate `isDualConsumingTemplate` en worker.js (líneas ~6121-6125).
  return (
    templateName === "VideoTemplate" ||
    templateName === "DualCTAVideoLanding" ||
    templateName === "BaseTemplateInternationalDual" ||
    templateName === "USExclusiveLanding"
  );
}

// Espejo del COUNTRY_TEMPLATE_MAP de cloudflare-worker-juaperrone/worker.js +
// src/config/workerCountryTemplateMap.ts. Cuando cambiás el worker, también
// actualizá acá — sino el simulador (/admin/dual-cta-variants), el
// experience-gaps analyzer y el dashboard impact map predicen el template
// equivocado y muestran warnings falsos (ej. "variant invisible en US" cuando
// el worker está sirviendo DualCTAVideoLanding y la variant SÍ se ve).
//
// Política actual del worker: AR → BaseTemplate, todo lo demás →
// USExclusiveLanding (default global). Sólo listamos AR (safety net); el
// fallback de resolveEffectiveTemplate cubre el resto. La fuente de verdad
// administrable son las filas locked en country_template_config (AR, FL, DEFAULT).
export const COUNTRY_DEFAULT_TEMPLATES: Readonly<Record<string, string>> = {
  AR: "BaseTemplate",
};

export function resolveEffectiveTemplate(
  countryCode: string,
  countryOverrides: Record<string, string | undefined>,
): string {
  const code = countryCode.toUpperCase();
  const override = countryOverrides[code];
  if (override) return override;
  // Paridad con worker.js resolveCountryTemplateOverride: country=DEFAULT
  // antes del fallback hardcodeado COUNTRY_TEMPLATE_MAP.
  const globalDefault = countryOverrides.DEFAULT;
  if (globalDefault) return globalDefault;
  const def = COUNTRY_DEFAULT_TEMPLATES[code];
  if (def) return def;
  return "USExclusiveLanding";
}

export interface CountryTemplateEntry {
  scope: string;
  geo_code: string;
  template: string;
  enabled?: boolean;
  /** DB column (mig 072) — AR / FL / DEFAULT protected rows. */
  locked?: boolean;
}

/**
 * Cascada visitor: city → state_us (US) → country → DEFAULT → fallback worker.
 * Espejo de worker.js resolveCountryTemplateOverride. AR queda fijo en
 * BaseTemplate (lock — el scope city de template rechaza ciudades AR en el
 * write, y este hardcode lo garantiza aunque una fila se cuele).
 *
 * `cityContext` es opcional (retrocompat): los callers que no manejan
 * granularidad ciudad siguen funcionando sin cambios.
 */
export function resolveEffectiveTemplateForVisitor(
  country: string,
  countryOverrides: Record<string, string>,
  stateOverrides: Record<string, string>,
  usState?: string | null,
  cityContext?: {
    city?: string | null;
    cityOverrides?: Record<string, string>;
  },
): string {
  const c = country.trim().toUpperCase();
  if (c === "AR") return "BaseTemplate";
  const cityKey = cityContext?.city ? normalizeCtaCityKey(cityContext.city) : "";
  if (cityKey && cityContext?.cityOverrides?.[cityKey]) {
    return cityContext.cityOverrides[cityKey];
  }
  if (c === "US" && usState) {
    const abbr = normalizeUsStateAbbr(usState);
    if (abbr && stateOverrides[abbr]) return stateOverrides[abbr];
  }
  return resolveEffectiveTemplate(c, countryOverrides);
}

export function buildCountryTemplateContext(entries: CountryTemplateEntry[]): {
  countryOverrides: Record<string, string>;
  stateOverrides: Record<string, string>;
  cityOverrides: Record<string, string>;
} {
  const countryOverrides: Record<string, string> = {};
  const stateOverrides: Record<string, string> = {};
  // Keys de city en forma canónica (normalizeCtaCityKey) — mismo contrato que
  // el matcher dual y el write del admin (geo_code ya se guarda canónico).
  const cityOverrides: Record<string, string> = {};
  for (const e of entries) {
    if (e.enabled === false || !e.template?.trim()) continue;
    if (e.scope === "city") {
      cityOverrides[normalizeCtaCityKey(e.geo_code)] = e.template;
      continue;
    }
    const code = e.geo_code.trim().toUpperCase();
    if (e.scope === "country") countryOverrides[code] = e.template;
    if (e.scope === "state_us") stateOverrides[code] = e.template;
  }
  return { countryOverrides, stateOverrides, cityOverrides };
}